/* Lab 1.3 · 传感器：同一次抓取，四种看法.

   One scripted grasp, nine seconds long: the hand comes down beside a ribbed cup, closes, lifts,
   gets a tap on the cup that nearly makes it slip, tightens, and puts it back. Four ways to sense it:
     彩色 the view camera as it is: it sees what things look like, not how far away they are;
     深度 the same camera through a depth shader: distance as colour, so the cup stands out from the floor;
     力   the normal force at each fingertip, from a simple contact model: to hold a cup of mass m with
          friction μ between two fingers, each finger must press at least N = (m g + extra) / 2μ;
     触觉 a 12 × 20 tactile pad on the left finger: where it touches, how hard, and the cup's ribs
          printed into it; when the cup slides, the print moves (slip) and the pad reports it.
   The numbers are the model's, not a real sensor's; the physics is only what the lesson needs. */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { C, q, recorded, createStage, floor, run, toScreen, clamp, span, ease, el } from "../kit/stage";
import { localizePage, tr, type Dict } from "../kit/i18n";
import STRINGS from "./i18n.json";

localizePage(STRINGS as Dict);

type View = "rgb" | "depth" | "force" | "tactile" | "all";
const CYCLE = 9;
const MASS = .25, G = 9.81, MU = .6, CUP_R = .04, CUP_H = .12, GRASP_Y = .07;
const HOLD = 3, TIGHT = 5, PUSH = 2;   // N per finger; N extra load from the tap

// --- the grasp as a function of its own time m (0..9 s)
interface Grasp { hand: THREE.Vector3; gap: number; force: number; lift: number; slip: number; push: number; need: number; slipping: boolean; phase: number }
function grasp(m: number): Grasp {
  const approach = ease(span(m, 0, 2.4));
  // the hand comes straight down from just above and behind the cup, in the middle of the frame: never under the
  // fingertip-force panel or the LIVE clock at the upper right (an approach from the camera's right put the near,
  // bright gripper behind that panel and over the top safe area). The drop is kept short (7 cm) so that, with the
  // short wrist below, the top of the hand never rises above the lift height: the whole gripper stays under the
  // title and the view label in every view (the near, amber-coloured depth view once ran it through both)
  const hand = new THREE.Vector3(0, GRASP_Y + .075 + .07 * (1 - approach), -.04 * (1 - approach));
  const closing = span(m, 2.4, 2.8), release = span(m, 8.0, 8.4);
  const gap = CUP_R + (.075 - CUP_R) * (1 - closing) + (.075 - CUP_R) * release;
  const push = m >= 5.5 && m < 5.95 ? PUSH * Math.sin(Math.PI * span(m, 5.5, 5.95)) : 0;
  let force = m < 2.8 ? 0 : HOLD * ease(span(m, 2.8, 3.3));
  force += (TIGHT - HOLD) * ease(span(m, 5.85, 6.3));
  force *= 1 - release;
  const lift = .08 * ease(span(m, 3.6, 4.8)) * (1 - ease(span(m, 7.4, 8.0)));
  const need = (MASS * G + push) / (2 * MU);
  const slipping = lift > .01 && force > 0 && force < need;
  const slip = .006 * ease(span(m, 5.62, 5.95));        // how far the cup slid while the grip was short
  hand.y += lift;
  const phase = m < 2.8 ? 0 : m < 3.6 ? 1 : m < 5.5 ? 2 : m < 5.95 ? 3 : m < 7.4 ? 4 : 5;
  return { hand, gap, force, lift, slip, push, need, slipping, phase };
}

// --- the scripted take: three segments, each a view and a slice of the grasp
function scripted(t: number): { view: View; m: number } {
  if (t < 8.5) return { view: "rgb", m: Math.min(t, CYCLE) };
  if (t < 16) { const l = t - 8.5; return { view: l < 3.3 ? "depth" : "force", m: Math.max(0, l - 1.8) }; }
  const l = t - 16;
  return { view: l < 5.4 ? "tactile" : "all", m: Math.min(CYCLE, l + 2.0) };
}

// --- scene
const canvas = el<HTMLCanvasElement>("stage");
const stage = createStage(canvas);
const { scene, camera, renderer, composer } = stage;
floor(scene);
const metal = (color: number, metalness = .75, roughness = .4) => new THREE.MeshStandardMaterial({ color, metalness, roughness });
const glow = (color: number, k = 2) => new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: k, roughness: .4 });

const cup = new THREE.Group();
{
  const glass = new THREE.MeshStandardMaterial({ color: 0x1b2a3a, metalness: .2, roughness: .35 });
  const body = new THREE.Mesh(new THREE.CylinderGeometry(CUP_R, CUP_R * .9, CUP_H, 64), glass);
  body.position.y = CUP_H / 2;
  body.castShadow = true;
  cup.add(body);
  const ribMat = metal(0x3b5166, .3, .5);
  for (let i = 0; i < 24; i++) {   // the ribs the tactile pad will feel
    const a = i / 24 * Math.PI * 2;
    const rib = new THREE.Mesh(new THREE.BoxGeometry(.004, CUP_H * .7, .003), ribMat);
    rib.position.set(Math.cos(a) * (CUP_R + .0005), CUP_H * .52, Math.sin(a) * (CUP_R + .0005));
    rib.rotation.y = -a;
    cup.add(rib);
  }
  const rim = new THREE.Mesh(new THREE.TorusGeometry(CUP_R, .003, 8, 64), glow(C.see, 1.2));
  rim.rotation.x = Math.PI / 2;
  rim.position.y = CUP_H;
  cup.add(rim);
}
scene.add(cup);

const hand = new THREE.Group();
const fingers: THREE.Group[] = [];
const padMat = glow(C.see, .4);
{
  const palm = new THREE.Mesh(new THREE.BoxGeometry(.2, .03, .05), metal(0x2c3444));
  palm.castShadow = true;
  // a short wrist ending in a mounting flange, so the gripper reads as a complete end effector inside the frame
  // (a 0.3 m wrist always ran off the top of the frame, through the view label and the title text)
  const wrist = new THREE.Mesh(new THREE.CylinderGeometry(.022, .022, .1, 24), metal(0x566274, .85, .32));
  wrist.position.y = .065;
  const flange = new THREE.Mesh(new THREE.CylinderGeometry(.034, .034, .012, 32), metal(0x3a4456, .8, .35));
  flange.position.y = .121;
  const collar = new THREE.Mesh(new THREE.TorusGeometry(.024, .004, 8, 32), glow(C.act, 1.2));
  collar.rotation.x = Math.PI / 2;
  collar.position.y = .03;
  hand.add(palm, wrist, flange, collar);
  for (const s of [-1, 1]) {
    const f = new THREE.Group();
    const bar = new THREE.Mesh(new THREE.BoxGeometry(.014, .085, .036), metal(0x9aa8bd, .8, .3));
    bar.position.y = -.055;
    bar.castShadow = true;
    const pad = new THREE.Mesh(new THREE.BoxGeometry(.004, .04, .03), s < 0 ? padMat : metal(0x121722, .5, .6));
    pad.position.set(-s * .009, -.075, 0);
    f.add(bar, pad);
    hand.add(f);
    fingers.push(f);
  }
}
scene.add(hand);

// force arrows at the fingertips, and the tap on the cup
const arrows = [-1, 1].map(s => { const a = new THREE.ArrowHelper(new THREE.Vector3(-s, 0, 0), new THREE.Vector3(), .1, C.act, .018, .012); scene.add(a); return a; });
const weightArrow = new THREE.ArrowHelper(new THREE.Vector3(0, -1, 0), new THREE.Vector3(), .06, C.muted, .014, .01);
const tapArrow = new THREE.ArrowHelper(new THREE.Vector3(0, -1, 0), new THREE.Vector3(), .06, C.amber, .02, .014);
scene.add(weightArrow, tapArrow);

// the depth view: every surface coloured by its distance to the camera, on the episode's scale
// (near amber #fbbf24 → mid blue #38bdf8 → far navy #101a2d; no red, as the brand rules ask)
const depthMat = new THREE.ShaderMaterial({
  uniforms: { near: { value: .55 }, far: { value: 1.6 } },
  vertexShader: "varying float d; void main(){ vec4 v = modelViewMatrix * vec4(position, 1.); d = -v.z; gl_Position = projectionMatrix * v; }",
  fragmentShader: `varying float d; uniform float near, far;
    vec3 scale(float x){ x = clamp(x, 0., 1.);
      vec3 farC = vec3(.063, .102, .176), midC = vec3(.220, .741, .973), nearC = vec3(.984, .749, .141);
      return x < .5 ? mix(farC, midC, x / .5) : mix(midC, nearC, smoothstep(.5, .85, x)); }
    void main(){ float k = 1. - (d - near) / (far - near);
      // past the near end of the window (the floor strip right in front of the lens) the colour keeps the near hue
      // but dims, so it reads as "nearer still, outside the window" without outshining the cup
      float b = k < 0. ? .15 : mix(1., .3, smoothstep(1., 1.12, k));
      gl_FragColor = vec4(scale(k) * b, 1.); }`,
});

// --- live controls
const controls = new OrbitControls(camera, canvas);
controls.target.set(0, .2, 0);
Object.assign(controls, { enableDamping: true, minDistance: .5, maxDistance: 2.4, maxPolarAngle: 1.5, enabled: !recorded });
camera.position.set(.25, .26, 1.12);
const user = { view: "rgb" as View, m: 0, playing: true, nums: true };
el("view").addEventListener("click", e => {
  const v = (e.target as HTMLElement).dataset.v as View | undefined;
  if (v) user.view = v;
});
const scrub = el<HTMLInputElement>("time");
scrub.addEventListener("input", () => { user.m = Number(scrub.value); user.playing = false; el<HTMLInputElement>("play").checked = false; });
el<HTMLInputElement>("play").addEventListener("change", e => { user.playing = (e.target as HTMLInputElement).checked; });
el<HTMLInputElement>("nums").addEventListener("change", e => { user.nums = (e.target as HTMLInputElement).checked; });
el("slip").addEventListener("click", () => { user.m = 5.2; user.playing = true; el<HTMLInputElement>("play").checked = true; });

// --- the tactile pad
const pad = el<HTMLCanvasElement>("pad"), px = pad.getContext("2d")!;
const ROWS = 12, COLS = 20;
function drawPad(g: Grasp): { cells: number } {
  px.fillStyle = "#070b14";
  px.fillRect(0, 0, pad.width, pad.height);
  const cw = pad.width / COLS, ch = pad.height / ROWS;
  const width = g.force > 0 ? 3 + 7 * clamp(g.force / TIGHT, 0, 1) : 0;    // a harder press, a wider contact
  const shift = g.slip / .001;                                              // 1 row per mm the cup slid
  let cells = 0;
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
    const dx = Math.abs(c - (COLS - 1) / 2) / Math.max(width / 2, 1e-6);
    let p = width ? Math.max(0, 1 - dx * dx) * g.force / TIGHT : 0;
    const rib = .3 + .7 * Math.max(0, Math.cos(2 * Math.PI * (r - shift) / 3));          // the cup's ribs, printed across the pad
    p *= rib * (r < 1 || r > ROWS - 2 ? .6 : 1);
    if (p > .06) cells++;
    const k = clamp(p, 0, 1);
    px.fillStyle = k < .04 ? "#0d1422" : `rgb(${Math.round(20 + 210 * k * k)}, ${Math.round(60 + 170 * k)}, ${Math.round(70 + 90 * k)})`;
    px.fillRect(c * cw + 1, r * ch + 1, cw - 2, ch - 2);
  }
  if (g.slipping || (g.slip > 0 && g.slip < .006)) {
    px.strokeStyle = "#fbbf24";
    px.lineWidth = 3;
    px.strokeRect(1.5, 1.5, pad.width - 3, pad.height - 3);
  }
  return { cells };
}

// --- one frame
function frameAt(t: number): void {
  let view: View, m: number;
  if (recorded) ({ view, m } = scripted(t));
  else {
    view = user.view;
    if (user.playing) { user.m = (user.m + 1 / 60) % CYCLE; scrub.value = user.m.toFixed(2); }
    m = user.m;
  }
  const g = grasp(m);

  cup.position.set(0, g.lift - g.slip, 0);
  hand.position.copy(g.hand);
  fingers.forEach((f, i) => { f.position.x = (i ? 1 : -1) * (g.gap + .007); });
  padMat.emissiveIntensity = .3 + 2 * clamp(g.force / TIGHT, 0, 1);
  arrows.forEach((a, i) => {
    const s = i ? 1 : -1;
    a.visible = g.force > .05 && (view === "force" || view === "all");
    a.position.set(s * (CUP_R + .06), GRASP_Y + g.lift, 0);
    a.setLength(Math.max(.02, g.force * .012), .018, .012);
  });
  weightArrow.visible = view === "force" && g.lift > .005;
  weightArrow.position.set(0, g.lift - .01, 0);
  tapArrow.visible = g.push > .05;
  tapArrow.position.set(0, CUP_H + g.lift + .07, 0);
  tapArrow.setLength(.03 + g.push * .02, .02, .014);

  if (recorded) {
    // low and to the side: the cup between the fingers, the floor behind. Framed so that the whole grasp, from the
    // cup's foot (y 0) to the flange at full lift (y ≈ 0.34 m), sits in the clear middle of the 1080p frame:
    // about y 325–850 px, below the title and the view label, above the subtitle band, and x ≈ 800–1210 px,
    // between the sensor panel on the left and the force/tactile panels on the right
    const az = -1.35 + t * .006, dist = 1.15;
    camera.position.set(Math.cos(az) * dist, .26, -Math.sin(az) * dist);
    camera.lookAt(controls.target.set(0, .2, 0));
  } else controls.update();

  // the depth colour window is set around the cup so that the cup reads as the nearest thing in view: its front face
  // and silhouette land at the amber (near) end, the hand coming down beside it too, and the floor behind it falls
  // through blue to dark navy (far). The floor strip between the lens and the cup is nearer still; it keeps the near
  // hue but is dimmed in the shader, so it no longer dominates the frame. (An earlier window centred 0.34 m in front
  // of the cup painted the cup mid blue, the series' "far" colour, under a bright amber foreground.)
  const toCup = camera.position.distanceTo(new THREE.Vector3(0, CUP_H / 2 + g.lift, 0));
  depthMat.uniforms.near.value = toCup - .05;
  depthMat.uniforms.far.value = toCup + .2;

  // draw: one view, or colour on the left and depth on the right
  const w = canvas.clientWidth, h = canvas.clientHeight;
  document.body.classList.toggle("dim", view === "rgb" || view === "depth");
  document.body.classList.toggle("solid", view === "depth" || view === "all");   // panels stay legible over the depth colours
  if (view === "all") {
    renderer.setScissorTest(true);
    renderer.setScissor(0, 0, w / 2, h);
    renderer.setViewport(0, 0, w, h);
    scene.overrideMaterial = null;
    renderer.render(scene, camera);
    renderer.setScissor(w / 2, 0, w / 2, h);
    scene.overrideMaterial = depthMat;
    renderer.render(scene, camera);
    scene.overrideMaterial = null;
    renderer.setScissorTest(false);
  } else {
    scene.overrideMaterial = view === "depth" ? depthMat : null;
    const bg = scene.background;
    if (view === "depth") scene.background = new THREE.Color(0x000000);
    (view === "depth" ? () => renderer.render(scene, camera) : () => composer.render())();
    scene.background = bg;
    scene.overrideMaterial = null;
  }

  // HUD
  el("clock").textContent = "T+" + t.toFixed(2).padStart(5, "0");
  el("dot").style.opacity = (t * 2 | 0) % 2 ? ".3" : "1";
  const names: Record<View, string> = { rgb: tr("RGB CAMERA · 彩色"), depth: tr("DEPTH · 深度"), force: tr("FORCE · 指尖力"), tactile: tr("TACTILE · 触觉"), all: tr("RGB | DEPTH · 同一时刻") };
  el("viewTag").textContent = names[view];
  document.querySelectorAll<HTMLButtonElement>("#view button").forEach(b => b.classList.toggle("on", b.dataset.v === view));
  const split = el("split");
  split.style.left = w / 2 - 1 + "px";
  split.style.opacity = view === "all" ? "1" : "0";
  el("tagL").style.left = w * .25 + "px"; el("tagR").style.left = w * .75 + "px";
  el("tagL").style.opacity = el("tagR").style.opacity = view === "all" ? "1" : "0";

  const cupCentre = new THREE.Vector3(0, CUP_H / 2 + g.lift - g.slip, 0);
  const sp = toScreen(cupCentre, camera, canvas);
  const dist = camera.position.distanceTo(cupCentre) - CUP_R;
  el("vRgb").textContent = `(${Math.round(sp.x / w * 640)}, ${Math.round(sp.y / h * 480)}) px`;
  el("vDepth").textContent = `${dist.toFixed(2)} m`;
  el("vForce").textContent = `${(2 * g.force).toFixed(1)} N`;
  const { cells } = drawPad(g);
  el("vTact").textContent = g.slipping ? tr("打滑！") : tr("{0} 格", cells);
  el("rRgb").classList.toggle("on", view === "rgb" || view === "all");
  el("rDepth").classList.toggle("on", view === "depth" || view === "all");
  el("rForce").classList.toggle("on", (view === "force" || view === "all") && g.force > .05);
  el("rTact").classList.toggle("on", (view === "tactile" || view === "all") && cells > 0 && !g.slipping);
  el("rTact").classList.toggle("warn", g.slipping);
  for (const [id, v] of [["fL", g.force], ["fR", g.force]] as const) {
    el(id).style.width = clamp(v / 6, 0, 1) * 100 + "%";
    el(id + "v").textContent = v.toFixed(1);
  }
  el("need").innerHTML = tr("托住杯子，每根手指至少要 {0}", `<b style="color:${g.force && g.force < g.need ? "var(--l3)" : "var(--ink)"}">${g.need.toFixed(1)} N</b>`) +
    (g.push > .05 ? ` <span style="color:var(--l3)">${tr("（被轻推了一下）")}</span>` : "");
  el("padArea").textContent = tr("接触 {0} 格", cells);
  el("padSlip").innerHTML = g.slipping ? `<span class="slip">${tr("! SLIP 打滑 {0} mm", (g.slip * 1000).toFixed(1))}</span>` : g.slip > 0 ? tr("滑了 {0} mm，已收紧", (g.slip * 1000).toFixed(1)) : "";
  document.querySelectorAll<HTMLElement>(".readings span").forEach(s => { s.style.visibility = recorded || user.nums ? "" : "hidden"; });
  scrub.value = m.toFixed(2);
}

run(frameAt, { dur: Number(q.get("dur") || 25.2), still: 3.5 });
